import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { configAPI, staffAPI, confidentialAPI } from '../services/api';
import toast from '../utils/toast';

// First field/non-field error message from a DRF validation error response.
const firstApiError = (error, fallback) => {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data.detail === 'string') return data.detail;
  const firstValue = Object.values(data)[0];
  return (Array.isArray(firstValue) ? firstValue[0] : firstValue) || fallback;
};

// Construction experience computed server-side from WPOL-imported WorkExperience records
export const useConstructionExperienceSummary = (staffId) => {
  return useQuery({
    queryKey: ['skills-upgrading', 'experience-summary', staffId],
    queryFn: async () => {
      const response = await staffAPI.getConstructionExperienceSummary(staffId);
      return response.data;
    },
    enabled: !!staffId,
    staleTime: 10 * 60 * 1000, // 10 minutes - WPOL imports are infrequent
  });
};

// Worker's classification record (skill level, levy, source country)
export const useWorkerClassification = (staffId) => {
  return useQuery({
    queryKey: ['skills-upgrading', 'classification', staffId],
    queryFn: async () => {
      const response = await configAPI.getConstructionWorkerClassifications({
        employee_staff_id: staffId,
      });
      const results = response.data?.results || [];
      return results.length > 0 ? results[0] : null;
    },
    enabled: !!staffId,
    // Eligibility M2M is refreshed by cert-verify signals; keep this fresh.
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
  });
};

// MOM upgrade pathways (configuration data - changes rarely)
export const useUpgradePathways = () => {
  return useQuery({
    queryKey: ['skills-upgrading', 'pathways'],
    queryFn: async () => {
      const response = await configAPI.getConstructionWorkerUpgradePathways({ current_only: 'true' });
      return response.data?.results || response.data || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Missing certifications per pathway, checked against the worker's verified certificates
export const useMissingCertifications = (classificationId) => {
  return useQuery({
    queryKey: ['skills-upgrading', 'missing-certifications', classificationId],
    queryFn: async () => {
      const response = await configAPI.getMissingCertifications(classificationId);
      return response.data;
    },
    enabled: !!classificationId,
    // Short stale window: HR verify of SEC(K)/CoreTrade must reflect quickly on
    // the Skills Upgrading tab (invalidateQueries still forces active refetch).
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
  });
};

// Verified certificate → levy pathway matches (review-only; never asserts BCA registration)
export const useCertificateEvidence = (staffId) => {
  return useQuery({
    queryKey: ['skills-upgrading', 'certificate-evidence', staffId],
    queryFn: async () => {
      const response = await configAPI.getCertificateEvidence(staffId);
      return response.data;
    },
    enabled: !!staffId,
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
  });
};

// HR-wide levy-optimization queue: at-risk registrations + upgrade opportunities (HR only)
export const useLevyOptimization = (params = {}) => {
  return useQuery({
    queryKey: ['skills-upgrading', 'levy-optimization', params],
    queryFn: async () => {
      const response = await configAPI.getConstructionLevyOptimization(params);
      return response.data;
    },
    // This is an operational HR queue: employment lifecycle changes must be
    // reflected when HR opens it, even if another page populated the cache.
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
    retry: false, // HR-only endpoint; a 403 for non-HR should not be retried
  });
};

// Truthful construction-status header: nationality-derived source country + actual levy paid
export const useWorkerConstructionStatus = (staffId) => {
  return useQuery({
    queryKey: ['skills-upgrading', 'construction-status', staffId],
    queryFn: async () => {
      const response = await configAPI.getWorkerConstructionStatus(staffId);
      return response.data;
    },
    enabled: !!staffId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Worker's current BCA registrations (CoreTrade / Multi-Skilling / Direct R1 / MBF)
export const useWorkerR1Registrations = (staffId) => {
  return useQuery({
    queryKey: ['skills-upgrading', 'r1-registrations', staffId],
    queryFn: async () => {
      const response = await configAPI.getWorkerR1Registrations(staffId);
      return response.data;
    },
    enabled: !!staffId,
    staleTime: 10 * 60 * 1000, // 10 minutes - registrations change infrequently
  });
};

// HR sets up a worker's construction classification (closes the "No Classification
// Record" dead end on the Skills Upgrading tab). Refreshes every queue/profile
// surface that reads classification data, since eligibility is computed server-side
// on create (see ConstructionWorkerClassificationViewSet.perform_create).
export const useCreateWorkerClassification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => configAPI.createConstructionWorkerClassification(data),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['skills-upgrading'] });
      if (variables?.employee_staff_id) {
        queryClient.invalidateQueries({
          queryKey: ['skills-upgrading', 'classification', variables.employee_staff_id],
        });
      }
      toast.success('Classification set up — eligibility assessed');
    },
    onError: (error) => {
      toast.error(firstApiError(error, 'Failed to set up classification'));
    },
  });
};

// Active salary record - backend scopes non-HR users to their own records
export const useWorkerActiveSalary = (staffId) => {
  return useQuery({
    queryKey: ['skills-upgrading', 'salary', staffId],
    queryFn: async () => {
      const response = await confidentialAPI.getEmployeeSalary({
        payroll_staff_id: staffId,
        payroll_is_active: true,
      });
      const results = response.data?.results || response.data;
      return (Array.isArray(results) ? results[0] : results) || null;
    },
    enabled: !!staffId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: false, // a 403 for non-self lookups should not be retried
  });
};
